import { Matrix, Vector } from 'mdatools/arrays';
import { pnorm, qnorm } from 'mdatools/distributions';
import { replacer, checkedResultOutliers } from '@mdatools-apps/shared/utils';
import { plsres_get } from '@mdatools-apps/wasm/pls.js';
import { seq } from '@mdatools-apps/shared/utils';


function getHard(yp, crit, ind, classNames, sensA, specA, effA) {

   const c0 = classNames[0];
   const c1 = classNames[1];

   const ind_members = ind[c0];
   const ind_strangers = ind[c1];

   let n = 0;
   for (let c of crit.v) {
      // members
      let TP = 0;
      for (let i of ind_members) {
         TP += yp[i] > c;
      }
      sensA[n] = TP / ind[c0].length;

      // strangers
      let TN = 0;
      for (let i of ind_strangers) {
         TN += yp[i] <= c;
      }
      specA[n] = TN / ind[c1].length;

      effA[n] = Math.sqrt(sensA[n] * specA[n])
      n += 1;
   }

}


function getSoft(yp, crit, ind, classNames, mc, sc, mr, sr,
   senscA, speccA, effcA, sensrA, specrA, effrA) {

   const nrows = yp.length;

   for (let ic of [0, 1]) {

      const compfun = ic == 0 ? (x, c) => x >= c : (x, c) => x < c

      const ind_members = ind[classNames[ic]];
      const ind_strangers = ind[classNames[1 - ic]];

      let n = 0;
      let sign = ic == 0 ? -1 : 1;
      for (let c of crit.v) {

         let cyc = mc[ic] + sign * c * sc[ic];
         let cyr = mr[ic] + sign * c * sr[ic];

         // members
         let TPc = 0;
         let TPr = 0;
         for (let i of ind_members) {
            TPc += compfun(yp[i], cyc);
            TPr += compfun(yp[i], cyr);
         }
         senscA[n] += TPc / nrows;
         sensrA[n] += TPr / nrows;

         // strangers
         let TNc = 0;
         let TNr = 0;
         for (let i of ind_strangers) {
            TNc += !compfun(yp[i], cyc);
            TNr += !compfun(yp[i], cyr);
         }

         speccA[n] += TNc / nrows;
         specrA[n] += TNr / nrows;
         n += 1;
      }
   }

   for (let n = 0; n < crit.length; n++) {
      effcA[n] = Math.sqrt(senscA[n] * speccA[n])
      effrA[n] = Math.sqrt(sensrA[n] * specrA[n])
   }

}

// the class order is the caller's array, never the keys of 'ind': 'Object.keys' lists a name
// which is an array index (a plain non-negative integer, '1' but not '-1' or '01') before the
// rest, in ascending numeric order, whatever order it was given in, so classes named '-1'
// and '1' came back swapped when the first row of the file was '-1', with the dummy's +1 on
// the second
export function getStatMatrices(plsres, daparams, ind, classNames) {

   const ncomp = plsres.ncomp;
   const nrows = plsres.nrows;


   const alpha = Vector.seq(0.990, 0.499, -0.010);
   const alphaTicks = [1, 6, 11, 16, 21, 26, 31, 36, 41, 46, 50];
   const alphaTicklabels = ['50', '45', '40', '35', '30', '25', '20', '15', '10', '5', '1']

   const threshTicks = [1, 11, 21, 31, 41, 51, 61, 71, 81, 91, 101]
   const threshTickLabels = seq(-10, 10, 2).map(v => (v / 10).toFixed(1));

   const crit_soft = qnorm(alpha);
   const crit_hard = Vector.seq(1, -1, -0.02);
   const nsoft = crit_soft.length;
   const nhard = crit_hard.length;


   const eff_soft_classic = Matrix.zeros(nsoft, ncomp);
   const sens_soft_classic = Matrix.zeros(nsoft, ncomp);
   const spec_soft_classic = Matrix.zeros(nsoft, ncomp);

   const eff_soft_robust = Matrix.zeros(nsoft, ncomp);
   const sens_soft_robust = Matrix.zeros(nsoft, ncomp);
   const spec_soft_robust = Matrix.zeros(nsoft, ncomp);

   const eff_hard = Matrix.zeros(nhard, ncomp);
   const sens_hard = Matrix.zeros(nhard, ncomp);
   const spec_hard = Matrix.zeros(nhard, ncomp);

   for (let a = 1; a <= ncomp; a++) {

      const yp = plsres_get(plsres, 'Yp', a, 1);

      const p0c = daparams[classNames[0]]['classic'];
      const m0c = p0c.m[a - 1];
      const s0c = p0c.s[a - 1];

      const p0r = daparams[classNames[0]]['robust'];
      const m0r = p0r.m[a - 1];
      const s0r = p0r.s[a - 1];

      const p1c = daparams[classNames[1]]['classic'];
      const m1c = p1c.m[a - 1];
      const s1c = p1c.s[a - 1];

      const p1r = daparams[classNames[1]]['robust'];
      const m1r = p1r.m[a - 1];
      const s1r = p1r.s[a - 1];

      getHard(yp, crit_hard, ind, classNames, sens_hard.getcolref(a), spec_hard.getcolref(a),
         eff_hard.getcolref(a));
      getSoft(yp, crit_soft, ind, classNames,
         [m0c, m1c],
         [s0c, s1c],
         [m0r, m1r],
         [s0r, s1r],
         sens_soft_classic.getcolref(a),
         spec_soft_classic.getcolref(a),
         eff_soft_classic.getcolref(a),
         sens_soft_robust.getcolref(a),
         spec_soft_robust.getcolref(a),
         eff_soft_robust.getcolref(a)
      );
   }

   return {
      'hard': {
         ticks: threshTicks,
         tickLabels: threshTickLabels,
         values: crit_hard,
         'stat': {
            sens: sens_hard,
            spec: spec_hard,
            eff: eff_hard
         }
      },
      'soft': {
         ticks: alphaTicks,
         tickLabels: alphaTicklabels,
         values: alpha,
         'stat': {
            'classic': {
               sens: sens_soft_classic,
               spec: spec_soft_classic,
               eff: eff_soft_classic
            },
            'robust': {
               sens: sens_soft_robust,
               spec: spec_soft_robust,
               eff: eff_soft_robust
            }
         }
      }
   };

}

export function getIndices(yr, classNames) {
   const ind1 = [];
   const ind2 = [];

   for (let i = 0; i < yr.length; i++) {
      if (yr[i] > 0) {
         ind1.push(i);
      } else {
         ind2.push(i);
      }
   }

   const out = {}
   out[classNames[0]] = ind1;
   out[classNames[1]] = ind2;
   return out;
}

export function classifySoft(yp, damodel, ind, im, ncomp) {

   const n = yp.length;
   const classNames = damodel.classNames;
   const is = im === 0 ? 1 : 0;

   let TP = 0;
   let FP = 0;
   let TN = 0;
   let FN = 0;

   const params = damodel.params[classNames[im]][damodel.limType];
   const m = params.m[ncomp];
   const s = params.s[ncomp];

   const d = new Array(n);
   const p = new Array(n);
   const alpha = damodel.alpha / 100;
   const compfun = im == 0 ? a => pnorm(a, m, s) : a => 1 - pnorm(a, m, s);

   if (!ind) {
      for (let i = 0; i < n; i++) {
         p[i] = compfun(yp[i]);
         d[i] = p[i] >= alpha;
      }
      return [TP, FN, FP, TN, d, p];
   }

   const ind_members = ind[classNames[im]]
   const ind_strangers = ind[classNames[is]]

   for (let i of ind_members) {
      p[i] = compfun(yp[i]);
      d[i] = p[i] >= alpha;
      TP += d[i];
      FN += 1 - d[i];
   }

   for (let i of ind_strangers) {
      p[i] = compfun(yp[i]);
      d[i] = p[i] >= alpha;
      TN += 1 - d[i];
      FP += d[i];
   }
   return [TP, FN, FP, TN, d, p];
}


export function classifyHard(yp, damodel, ind, im, ncomp) {

   const n = yp.length;
   const threshold = damodel.threshold;
   const classNames = damodel.classNames;
   const is = im === 0 ? 1 : 0;

   let TP = 0;
   let FP = 0;
   let TN = 0;
   let FN = 0;

   const d = new Array(n);
   const p = new Array(n);
   const compfun = im == 0 ? a => a > threshold : a => a <= threshold;

   if (!ind) {
      for (let i = 0; i < n; i++) {
         d[i] = compfun(yp[i]);
      }
      return [TP, FN, FP, TN, d, p];
   }

   const ind_members = ind[classNames[im]]
   const ind_strangers = ind[classNames[is]]

   for (let i of ind_members) {
      d[i] = compfun(yp[i]);
      p[i] = d[i];
      TP += d[i]
      FN += 1 - d[i];
   }

   for (let i of ind_strangers) {
      d[i] = compfun(yp[i]);
      p[i] = d[i];
      TN += 1 - d[i]
      FP += d[i];
   }

   return [TP, FN, FP, TN, d, p];
}

export function predict(plsres, damodel, ind) {

   const ncomp = plsres.ncomp;
   const nrows = plsres.nrows;
   const classNames = damodel.classNames;
   const nclasses = classNames.length;

   const v = {};
   const classifyFun = damodel.rule === 'hard' ? classifyHard : classifySoft;
   const hasReference = ind !== null

   const total_sensitivity = new Array(ncomp).fill(0);
   const total_specificity = new Array(ncomp).fill(0);
   const total_efficiency  = new Array(ncomp).fill(0);
   const total_accuracy    = new Array(ncomp).fill(0);


   for (let ic = 0; ic < nclasses; ic++) {

      const res = new Array(ncomp);

      const sensitivity = new Array(ncomp);
      const specificity = new Array(ncomp);
      const efficiency  = new Array(ncomp);
      const accuracy    = new Array(ncomp);

      const nc = hasReference ? ind[classNames[ic]].length : 1;
      const pc = nc / nrows;
      const pm = 1 - pc;

      for (let a = 0; a < ncomp; a++) {
         const yp = plsres_get(plsres, "Yp", a + 1, 1);
         const [TP, FN, FP, TN, d, p] = classifyFun(yp, damodel, ind, ic, a);

         if (hasReference) {
            sensitivity[a] = TP > 0 ? TP / (TP + FN) : 0;
            specificity[a] = TN > 0 ? TN / (TN + FP) : 0;
            efficiency[a] = Math.sqrt(sensitivity[a] * specificity[a]);
            accuracy[a] = TP + TN + FP + FN > 0 ? (TP + TN) / (TP + TN + FP + FN) : 0;

            total_sensitivity[a] += sensitivity[a] * pc;
            total_specificity[a] += specificity[a] * pm;
         }

         res[a] = {TP, FN, FP, TN, d, p, yp: new Vector(yp)};
      }

      const stat = {sensitivity, specificity, efficiency, accuracy};
      v[classNames[ic]] = {res, stat};
   }

   for (let a = 0; a < ncomp; a++) {
      total_efficiency[a]  = Math.sqrt(total_sensitivity[a] * total_specificity[a]);
      total_accuracy[a]    = (total_sensitivity[a]  + total_specificity[a]) / 2;
   }

   v['total'] = {stat: {sensitivity: total_sensitivity, specificity: total_specificity, efficiency: total_efficiency, accuracy: total_accuracy}};

   return {
      class: ['dares'],
      objlabels: plsres.objlabels,
      threshold: damodel.threshold,
      rule: damodel.rule,
      alpha: damodel.alpha,
      crit: damodel.crit,
      params: damodel.params,
      limType: damodel.limType,
      ind,
      nclasses,
      classNames,
      nrows,
      ncomp,
      v,
   }
}


/**
 * Creates CSV file with PLS result outcomes and download it as a file.
 *
 * @param {Object} resall - object with PLS1DA results (both PLS and DA).
 * @param {Object} dataset - object with dataset used to get the results.
 * @param {string} name - name of the results (e.g. 'cal').
 */
export function downloadResults(res, dataset, name, nCompSelected) {

   // an entry that names no real row is silent: it matches no whole 'i' below, so a row the
   // result has no values for is emitted and the file is written misaligned. see
   // 'checkedResultOutliers' for what is refused and why none of it is caught further down
   const outliers = checkedResultOutliers(res.pls.outliers, dataset.values.nrows);
   let exclvars = res.pls.exclvars;

   if (!exclvars || !Array.isArray(exclvars)) {
      exclvars = [];
   }

   // local copy for separators
   const sep = dataset.sep;
   const dec = dataset.dec;

   // every value is converted on its own. the exporter used to run one replace over the finished
   // document instead, which rewrote every dot in it: an object label 'sample.1' was exported as
   // 'sample,1', while the file name and the title were added after the replace and kept theirs,
   // so one name could be spelled two ways in the same file. only a number is converted here; a
   // string - a label, a heading or the dash of an excluded row - keeps whatever it contains
   const num2str = v => typeof v === 'number' ? String(v).replace('.', dec) : v;
   const row = values => values.map(num2str).join(sep) + sep + '\n';

   // the conversion the whole document had, kept for these lines alone: they hold a model field
   // and nothing a user named, so a dot on them is converted as it was
   const line = values => values.join(sep).replace(/\./g, dec) + '\n';

   // number of rows with/without outliers. 'n' is how many rows every block read below holds,
   // so it has to be what the result actually holds. if that disagrees with the dataset the
   // file would come out silently misaligned, which is worse than not producing it at all
   const nAll = dataset.values.nrows;
   const nOut = outliers.length;
   const n = res.pls.nrows;

   if (nAll - nOut !== n) {
      throw new Error(
         'Results do not match the dataset: ' + nAll + ' rows minus ' + nOut +
         ' excluded is not ' + n + '.'
      );
   }

   // number of classes and components
   const nComp = res.pls.ncomp;
   const classNames = res.da.classNames;
   const hasReference = res.da.ind !== null;

   // number of empty columns to add in addition to row name and first value
   const filler = Array(nComp).fill('');

   // vector with component names/labels and values separator
   const compNames = Array(nComp).fill(0).map((v, i) => 'LV' + (i + 1));

   // general information
   let out = '';
   out += ['Number of LVs:', nComp, ...filler].join(sep) + '\n';
   out += ['Optimal number of LVs:', nCompSelected, ...filler].join(sep) + '\n';
   out += line(['Center:', res.pls.center, ...filler]);
   out += line(['Scale:', res.pls.scale, ...filler]);

   if (outliers.length > 0) {
      out += ['Removed outliers:', outliers.length, ...filler].join(sep) + '\n';
   }

   if (exclvars.length > 0) {
      out += ['Excluded variables:', exclvars.length, ...filler].join(sep) + '\n';
   }

   out += '\n';
   out += ['Classification rule:', res.da.rule, ...filler].join(sep) + '\n';


   if (res.da.rule === 'soft') {
      out += line(['Alpha (%):', res.da.alpha, ...filler]);
      out += ['Limits:', res.da.limType, ...filler].join(sep) + '\n';

      for (let c of classNames) {
         const crit = res.da.crit[c];
         out += '\n';
         out += ['Class:' + sep + c, ...filler].join(sep) + '\n';
         out += line(['Mean:', crit[0], ...filler]);
         out += line(['Deviation:', crit[1], ...filler]);
         out += line(['Critical value:', crit[2], ...filler]);
      }
   } else {
      out += line(['Threshold:', res.da.threshold, ...filler]);
   }


   // variances
   out += '\n';
   if (hasReference) {
      out += ['Variances:', '', ...filler].join(sep) + '\n';
      out += ['', ...compNames].join(sep) + sep + '\n';
      out += row(['xexpvar:', ...res.pls.stat.xexpvar]);
      out += row(['xcumexpvar', ...res.pls.stat.xcumexpvar]);

      out += row(['yexpvar:', ...res.pls.stat.yexpvar]);
      out += row(['ycumexpvar:', ...res.pls.stat.ycumexpvar]);
      out += row(['RMSE:', ...res.pls.stat.rmse.v]);
      out += '\n';

      // the counts of every LV above the figures of merit; Total sums them over the classes
      const COUNTS = ['TP', 'TN', 'FP', 'FN'];
      for (let c of classNames) {
         const stats = res.da.v[c].stat;

         out += ['Class:' + sep + c, ...filler].join(sep) + '\n';
         out += ['FoMs:', ...compNames].join(sep) + sep + '\n';
         for (const key of COUNTS) out += row([key, ...res.da.v[c].res.map(r => r[key])]);
         out += row(['Sensitivity', ...stats.sensitivity]);
         out += row(['Specificity', ...stats.specificity]);
         out += row(['Efficiency', ...stats.efficiency]);
         out += row(['Accuracy', ...stats.accuracy]);
         out += '\n';
      }

      const stats = res.da.v['total'].stat;
      out += ['Total:' + sep + '', ...filler].join(sep) + '\n';
      out += ['FoMs:', ...compNames].join(sep) + sep + '\n';
      for (const key of COUNTS) {
         out += row([key, ...compNames.map((v, a) => classNames.reduce((s, c) => s + res.da.v[c].res[a][key], 0))]);
      }
      out += row(['Sensitivity', ...stats.sensitivity]);
      out += row(['Specificity', ...stats.specificity]);
      out += row(['Efficiency', ...stats.efficiency]);
      out += row(['Accuracy', ...stats.accuracy]);
      out += '\n';
   }


   // Predictions:
   // ------------
   // n - is number of rows in result objects without outliers
   // j - is an iterator along these rows
   // nAll - is a number of rows in original dataset
   // i - is an iterator along these rows
   let j = 0;
   const rowlabels = dataset.objlabels;
   const yref = hasReference ? res.pls.Yref.getcolref(1) : null;

   // get decisions and p-values
   const res1a = res.da.v[classNames[0]].res[nCompSelected - 1];
   const res2a = res.da.v[classNames[1]].res[nCompSelected - 1];

   if (res.da.rule === 'soft') {
      const nFields = 7;
      out += [
         'object',
         'class',
         'y (reference)',
         'y (predicted)',
         'decision (' + classNames[0] + ')',
         'p-score (' + classNames[0] + ')',
         'decision (' + classNames[1] + ')',
         'p-score (' + classNames[1] + ')',
      ].join(sep) + sep + '\n';

      for (let i = 0; i < nAll; i++) {

         // outlier
         if (nOut > 0 && outliers.includes(i)) {
            out += row([rowlabels[i], ...Array(nFields).fill('-')]);
            continue;
         }


         out += row([
            rowlabels[i],
            hasReference ? (yref[j] > 0 ? classNames[0] : classNames[1]) : '-',
            hasReference ? yref[j] : '-',
            res1a.yp.v[j],
            res1a.d[j] ? 'in' : 'out',
            res1a.p[j],
            res2a.d[j] ? 'in' : 'out',
            res2a.p[j],

         ]);
         j = j + 1;
      }
   } else {
      const nFields = 4;
      out += [
         'object',
         'class',
         'y (reference)',
         'y (predicted)',
         'decision',
      ].join(sep) + sep + '\n';

      for (let i = 0; i < nAll; i++) {

         // outlier
         if (nOut > 0 && outliers.includes(i)) {
            out += row([rowlabels[i], ...Array(nFields).fill('-')]);
            continue;
         }

         out += row([
            rowlabels[i],
            hasReference ? (yref[j] > 0 ? classNames[0] : classNames[1]) : '-',
            hasReference ? yref[j] : '-',
            res1a.yp.v[j],
            res1a.d[j] ? classNames[0] : classNames[1],
         ]);
         j = j + 1;
      }
   }
   out += '\n';


   // add header
   out = ['Data filename:', dataset.fileName, ...filler].join(dataset.sep) + '\n' + out;

   let hdr = ['PLS1-DA results (' + name + ')', 'https://mda.tools/pls1da/', ...filler].join(dataset.sep) + '\n\n';

   // create Blob and start download process
   const blob = new Blob([hdr + out], { type: 'text/csv' });
   const url = window.URL.createObjectURL(blob)
   const a = document.createElement('a')
   a.setAttribute('href', url)
   a.setAttribute('download', `pls1da-results-${name}.csv`);
   a.click()
   URL.revokeObjectURL(url)
}


/**
 * Creates JSON with model object, stringify it and download as a file.
 *
 * @param {Object} model - object with PLS model
 */
export function downloadModel(model, prepmodel) {

   const modelWithPrep = {...model, pls: {...model.pls, prep: prepmodel}};
   const blob = new Blob([JSON.stringify(modelWithPrep, replacer)], { type: 'application/json' });
   const url = window.URL.createObjectURL(blob)
   const a = document.createElement('a')
   a.setAttribute('href', url)
   a.setAttribute('download', 'pls1da-model.json');
   a.click()
   URL.revokeObjectURL(url)
}
